import QueryString from 'query-string';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import ReactTable from "react-table";
import { Redirect } from 'react-router-dom';
import Select from 'react-select';
import i18next from 'i18next';

import { RowButton } from '../RowButton';
import { AccessHelper, BaseTable } from '../../helpers';
import { GroupingsService, GroupsService } from '../../services';
import { Modal } from 'react-bootstrap';

const AddChildButtons = ({ grouping, addClick }) => (
  grouping.deviceGroup.children.map((child, index) =>
    <span key = {index}>
      <button className="btn btn-sm btn-default" onClick={() => addClick(grouping, child)}>{i18next.t('add')} {child.name}</button> &nbsp;
    </span>
  )
);

const GroupingRowButton = ({ grouping, viewAllowed, editAllowed, addAllowed, moveAllowed, floorPlanAllowed, viewPath, editPath, addLinkClick, floorPlanLinkClicked, archiveClicked, restoreClicked, moveClicked }) => {
  const isArchived = grouping.archived ?? false;
  const parentIsArchived = grouping.parent?.archived ?? false;
  const hasChildren = grouping.children?.length > 0;
  const hasParent = grouping.parent ? true : false;

  return (
    <RowButton allowView={viewAllowed} allowEdit={editAllowed} viewPath={viewPath} editPath={editPath}>

      { addAllowed && <AddChildButtons addClick={addLinkClick} grouping={grouping} /> }

      { floorPlanAllowed && <button className="btn btn-sm btn-default" onClick={() => floorPlanLinkClicked(grouping.id)}>Floor Plan</button> }

      &nbsp;

      { moveAllowed && hasParent && <button className="btn btn-sm btn-default" onClick={() => moveClicked(grouping)}>Move</button> }

      &nbsp;&nbsp;&nbsp;

      { editAllowed && !isArchived && !hasChildren && <button className="btn btn-sm btn-danger" onClick={() => archiveClicked(grouping)}>Archive</button> }

      { editAllowed && isArchived && !parentIsArchived && <button className="btn btn-sm btn-danger" onClick={() => restoreClicked(grouping.id)}>Restore</button> }
      { editAllowed && isArchived && parentIsArchived && <span>Parent Archived</span>}

    </RowButton>
  );
}

const MoveGroupingModal = ({ groupingToMove, showMoveModal, hideMoveModal, executeMove }) => {
  const [parentGroupingOptions, setParentGroupingOptions] = useState(null);
  const [selectedGroupingToMoveTo, setSelectedGroupingToMoveTo] = useState(null);
  const [selectedBusinessStructureToMoveTo, setSelectedBusinessStructureToMoveTo] = useState(null);
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    GroupingsService.getMoveTargetsFor(groupingToMove.id).then(({ groupings }) => {
      setParentGroupingOptions(groupings ? groupings.map((grouping) => ({ value: grouping.id, label: grouping.name, grouping: grouping })) : []);

      setLoading(false);
    }).catch(error => {
      console.error(error);
      setLoading(false);
    });
  }, [groupingToMove]);

  const handleGroupingSelection = useCallback(({ grouping }) => {
    setSelectedGroupingToMoveTo(grouping);
    
    if (grouping.deviceGroup.children?.length == 1) {
      setSelectedBusinessStructureToMoveTo(grouping.deviceGroup.children[0]);
      setReady(true);
    }
  }, [parentGroupingOptions]);

  const handleBusinessStructureSelection = useCallback(({ group }) => {
    setSelectedBusinessStructureToMoveTo(group);
    setReady(true);
  }, [parentGroupingOptions]);

  return (
    <Modal show={showMoveModal} onHide={hideMoveModal}>
      <Modal.Header closeButton>
        <Modal.Title>Confirm Move</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ minHeight: 300 }}>
        { loading && <div className='loader'>Loading...</div>}

        { !loading && parentGroupingOptions.length == 0 && 
          <div>There are no available groups to move to.</div>
        }

        { !loading && parentGroupingOptions.length > 0 &&
          <>
            <p>Move "<b>{groupingToMove.name}</b>" from "<b>{groupingToMove.parent.name}</b>" to</p>
            <Select
              className="react-select-container"
              classNamePrefix="react-select"
              options={parentGroupingOptions}
              name="selected-filter-by"
              onChange={handleGroupingSelection}
              searchable={true}
              placeholder={i18next.t('select')}
            />

            { selectedGroupingToMoveTo && selectedGroupingToMoveTo.deviceGroup.children?.length > 1 &&
              <div>
                <br/>
                Business Structure Selection Required:<br/>
                <Select
                  className="react-select-container"
                  classNamePrefix="react-select"
                  options={selectedGroupingToMoveTo.deviceGroup.children.map((group) => ({ value: group.id, label: group.name, group: group}))}
                  name="selected-filter-by"
                  onChange={handleBusinessStructureSelection}
                  searchable={true}
                  placeholder={i18next.t('select')}
                />
              </div>
            }
          </>
        }

        { ready &&
         <div>
          <br/>
          <b>Please review the following:</b>
          <br/>
          "<b>{groupingToMove.name}</b>" will be moved from "<b>{groupingToMove.parent.name}</b>" to 
          "<b>{selectedGroupingToMoveTo.name}</b>" as a "<b>{selectedBusinessStructureToMoveTo.name}</b>"
         </div>
        }
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-sm btn-danger" onClick={() => executeMove(groupingToMove, selectedGroupingToMoveTo, selectedBusinessStructureToMoveTo.id)} disabled={!ready}>Move</button>
        <button className="btn btn-sm btn-default" onClick={hideMoveModal}>{i18next.t('form-labels.cancel')}</button>
      </Modal.Footer>
    </Modal>
  );
}

@connect(state => {
  return {
    me: state.user.me
  }
})
class GroupingList extends React.Component {
  constructor(props) {
    super(props);

    const passedState = this.props.currLocation ? this.props.currLocation.state : null;

    this.DEFAULT_SORTED = [{ id: 'name', desc: false }];

    this.fetchData = this.fetchData.bind(this);
    this.datafetched = this.datafetched.bind(this);
    this.makeDatatableRequest = this.makeDatatableRequest.bind(this);
    this.updateRequestURL = this.updateRequestURL.bind(this);

    this.state = {
      linkTo: null,
      backLinkSearch: null,
      query: null,
      localErrorHandler: this.props.localErrorHandler,
      localErrorMessage: null,
      actionCallback: this.props.actionCallback,
      actionTitle: this.props.actionTitle,
      groupFilterOptions: passedState ? passedState.groupFilterOptions : null,
      selectedFilterByOption: passedState ? passedState.selectedFilterByOption : null,
      data: [],
      pages: null,
      forcePage: null,
      loading: true,
      totalElements: null,
      parsedOptions: QueryString.parse(this.props.currSearch),
      currentHistoryCount: passedState && passedState.currentHistoryCount ? passedState.currentHistoryCount : 0,
      viewAllowed: AccessHelper.userHasAccess(this.props.me.availableRoutes, '/admin/groupings/view/:deviceGroupingId'),
      editAllowed: AccessHelper.userHasAccess(this.props.me.availableRoutes, '/admin/groupings/edit/:deviceGroupingId'),
      addAllowed: AccessHelper.userHasAccess(this.props.me.availableRoutes, '/admin/groupings/add'),
      floorPlanAllowed: AccessHelper.userHasAccess(this.props.me.availableRoutes, '/admin/groupings/:deviceGroupingId/floor-plan')
    };
  }

  componentDidMount() {
    const parsedOptions = this.state.parsedOptions ? this.state.parsedOptions : {};

    if (this.state.groupFilterOptions == null) {
      GroupsService.listAll().then(response => {
        let selectedFilterByOption = response.groups.find(group => group.parentId === 0);
        if (parsedOptions['filterByGroupId']) {
          selectedFilterByOption = response.groups.find(group => group.id == parsedOptions['filterByGroupId']);
        } else if (selectedFilterByOption == null) {
          selectedFilterByOption = response.groups[0];
        }

        this.setState({
          groupFilterOptions: response.groups,
          selectedFilterByOption: selectedFilterByOption
        });
      }).catch(error => {
        console.error(error);
        this.state.localErrorHandler(error.message);
      });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.table) {
      this.table.ajax.reload();
    }
  }

  componentDidUpdate(prevProps, prevState, prevContext) {
    if (this.table) {
      this.table.ajax.reload();
    }
  }

  addLinkClick(grouping, selected) {
    this.setState({
      linkTo: `/admin/groupings/add`,
      query: { parentGrouping: grouping, selected: selected },
      backLinkSearch: this.props.currLocation.search
    });
  }

  floorPlanLinkClicked(rowId) {
    this.setState({
      linkTo: `/admin/groupings/${rowId}/floor-plan`,
      backLinkSearch: this.props.currLocation.search
    })
  }

  filterByChanged(filterByOption) {
    this.setState({
      selectedFilterByOption: filterByOption
    }, () => {
      this.updateRequestURL(this.refs.groupingsTable && this.refs.groupingsTable.state ? this.refs.groupingsTable.state : null);
    });
  }

  datafetched(res) {
    let size = this.refs.groupingsTable.state.pageSize;
    let page = this.refs.groupingsTable.state.page;
    const pageCount = Math.ceil(res.recordsTotal / size)
    let forcePage = null;
    if (page >= pageCount && pageCount > 0) {
      forcePage = pageCount - 1;
    }

    this.setState({
      data: res.data,
      pages: pageCount,
      page: page,
      pageSize: size,
      forcePage: forcePage,
      loading: false,
      totalElements: res.recordsTotal,
      showArchived: false,
      showDeleteModal: false,
      groupingToArchive: null,
      showMoveModal: false,
      groupingToMove: null,
    });
  }

  fetchData(tableData) {
    if (this.updateRequestURL(tableData)) {
      return;
    }

    this.setState({
      loading: true
    });

    this.makeDatatableRequest(tableData, this.datafetched, null);
  }

  makeDatatableRequest (data, callback, settings) {
    let options = BaseTable.buildFetchDataOptions(this.state.parsedOptions, this.DEFAULT_SORTED);

    options.push({ filterByGroupId: this.state.selectedFilterByOption.id });

    GroupingsService.getAll(options, this.state.showArchived ?? false).then(response => {
      callback({
        data: response.groupings,
        recordsTotal: response.page.totalElements,
        recordsFiltered: response.page.totalElements
      });
    }).catch(error => {
      console.error(error);
      this.setState({ hasError: true });
      this.state.localErrorHandler(error.message);
    });
  }

  updateRequestURL(tableData) {
    let options = BaseTable.buildURLOptions(tableData, this.DEFAULT_SORTED);

    if (this.state.selectedFilterByOption && this.state.selectedFilterByOption.id != this.state.groupFilterOptions[0].id) {
      options.push({ 'filterByGroupId': this.state.selectedFilterByOption.id });
    }

    let params = options.map(option => Object.keys(option).map((k, i) => `${k}=${option[k]}`)).join('&');

    const currentSearch = this.props.currSearch ? this.props.currSearch : '?';

    if (`?${params}` !== currentSearch) {
      if (this.state.currentHistoryCount > 100) {
        console.error('Exceeded the history count stack of 100.');
        return false;
      }
      this.props.pushHistory({
        search: `?${params}`,
        state: {
          currentHistoryCount: this.state.currentHistoryCount + 1,
          groupFilterOptions: this.state.groupFilterOptions,
          selectedFilterByOption: this.state.selectedFilterByOption
        }
      });
      return true;
    } else {
      return false;
    }
  }

  executeArchive() {
    this.setState({ loading: true });

    GroupingsService.archive(this.state.groupingToArchive.id).then(() => {
      this.setState({ loading: false, groupingToArchive: null }, () => this.fetchData());
    }).catch((err) => {
      console.error(err);
      this.setState({ loading: false, groupingToArchive: null });
    });
  }

  archiveClicked(groupingToArchive) {
    this.setState({ showDeleteModal: true, groupingToArchive: groupingToArchive });
  }

  restoreClicked(groupingId) {
    this.setState({ loading: true });

    GroupingsService.restore(groupingId).then(() => {
      this.setState({ loading: false }, () => this.fetchData());
    }).catch((err) => {
      console.error(err);
      this.setState({ loading: false });
    });
  }

  toggleShowArchive(checkboxValue) {
    this.setState({ showArchived: checkboxValue }, () => this.fetchData());
  }

  hideDeleteModal() {
    this.setState({ showDeleteModal: false });
  }

  moveClicked(groupingToMove) {
    this.setState({ showMoveModal: true, groupingToMove: groupingToMove });
  }

  executeMove(groupingToMove, toParentGrouping, toParentGroupingGroupId) {
    this.hideMoveModal();

    this.setState({ loading: true });

    GroupingsService.move(groupingToMove.id, toParentGrouping.id, toParentGroupingGroupId).then(() => {
      this.setState({ loading: false, groupingToMove: null }, () => this.fetchData());
    }).catch((err) => {
      console.error(err);
      this.setState({ loading: false });
    });
  }

  hideMoveModal() {
    this.setState({ showMoveModal: false });
  }

  render() {
    const { data, pages, loading, totalElements } = this.state;

    if (this.state.linkTo) {
      if (this.state.query) {
        return <Redirect push to={{ pathname: this.state.linkTo, query: this.state.query, state: { backLinkSearch: this.state.backLinkSearch }}} />;
      } else {
        return <Redirect push to={{ pathname: this.state.linkTo, state: { backLinkSearch: this.state.backLinkSearch }}} />;
      }
    }

    let dynamicOptions = BaseTable.buildDynamicOptions(this.state.parsedOptions, this.state.forcePage, this.DEFAULT_SORTED);

    if (this.state.groupFilterOptions && this.state.selectedFilterByOption ) {
      let floorPlanAllowed = this.state.floorPlanAllowed && this.state.selectedFilterByOption.groupAssociableMetadataTypes.find(type => type.token === 'floor_plan_location')
      return (
        <div>
          { this.state.groupingToArchive && 
            <Modal show={this.state.showDeleteModal} onHide={this.hideDeleteModal.bind(this)}>
              <Modal.Header closeButton>
                <Modal.Title>Confirm Archive</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <p>Are you sure you wish to archive "{this.state.groupingToArchive.name}" ?</p> <br />
              </Modal.Body>
              <Modal.Footer>
                <button className="btn btn-sm btn-danger" onClick={this.executeArchive.bind(this)}>Archive</button>
                <button className="btn btn-sm btn-default" onClick={this.hideDeleteModal.bind(this)}>{i18next.t('form-labels.cancel')}</button>
              </Modal.Footer>
            </Modal>
          }
          
          { this.state.groupingToMove && 
            <MoveGroupingModal 
              groupingToMove={this.state.groupingToMove} 
              allGroupings={data} 
              showMoveModal={this.state.showMoveModal} 
              hideMoveModal={this.hideMoveModal.bind(this)} 
              executeMove={this.executeMove.bind(this)} 
            />
          }

          <div className='row'>
            <div className='col-sm-8'>
              <label>{i18next.t('form-labels.group-children')}</label>
              <div className="add-margin-bottom short-selector">
                <GroupingGroupFilter
                  groupFilterOptions={this.state.groupFilterOptions}
                  initialSelection={this.state.selectedFilterByOption}
                  onFilterByChange={this.filterByChanged.bind(this)}
                />
              </div>
            </div>
            <div className='col-sm-4'>
              <label>Show Archived</label>
              <div className="add-margin-bottom short-selector">
                <input type="checkbox" onChange={(e) => this.toggleShowArchive(e.target.checked)} checked={this.showArchived} />
              </div>
            </div>
          </div>

          <div className="react-table-custom">
            <ReactTable
              className="-striped -highlight"
              ref="groupingsTable"
              manual // Forces table not to paginate or sort automatically, so we can handle it server-side
              data={data}
              pages={pages} // Display the total number of pages
              loading={loading} // Display the loading overlay when we need it
              onFetchData={this.fetchData} // Request new data when things change
              totalElements={totalElements}
              defaultSorted={[{
                id: 'name',
                desc: false // not necessary unless set to true, but it's good to know its there.
              }]}
              showPaginationTop
              columns={[
                          {
                            Header: i18next.t('table-headers.name'),
                            id: "name",
                            accessor: "name",
                            sortable: true,
                            minResizeWidth: 50,
                            width: 250
                          },
                          {
                            Header: i18next.t('table-headers.parent'),
                            accessor: "parent.name",
                            Cell: cell => <span>{cell.value ? cell.value : i18next.t('none')}</span>,
                            sortable: true,
                            minResizeWidth: 50,
                            width: 250
                          },
                          {
                            Header: i18next.t('table-headers.alerts'),
                            accessor: "actionsEnabled",
                            Cell: cell => <span>{cell.value === true ? i18next.t('enabled') : i18next.t('disabled')}</span>,
                            sortable: false,
                            minResizeWidth: 50,
                            width: 100
                          },
                          {
                            Cell: cell => <GroupingRowButton
                                              grouping={cell.original}
                                              viewAllowed={this.state.viewAllowed}
                                              editAllowed={this.state.editAllowed}
                                              addAllowed={this.state.addAllowed}
                                              moveAllowed={this.state.editAllowed && cell.original.children?.length == 0}
                                              floorPlanAllowed={floorPlanAllowed}
                                              viewPath={{
                                                pathname: `/admin/groupings/view/${cell.original.id}`,
                                                state: { backLinkSearch: this.props.currLocation.search }
                                              }}
                                              editPath={{
                                                pathname: `/admin/groupings/edit/${cell.original.id}`,
                                                state: { backLinkSearch: this.props.currLocation.search }
                                              }}
                                              addLinkClick={this.addLinkClick.bind(this)}
                                              floorPlanLinkClicked={this.floorPlanLinkClicked.bind(this)}
                                              archiveClicked={this.archiveClicked.bind(this)}
                                              restoreClicked={this.restoreClicked.bind(this)}
                                              moveClicked={this.moveClicked.bind(this)}
                                          />,
                            sortable: false,
                            minResizeWidth: 50
                          }
                        ]}
              {...dynamicOptions}
            />
          </div>
        </div>
      );
    } else {
      return (
        <div className="loader">{i18next.t('loading')}</div>
      );
    }
  }
}

const GroupingGroupFilter = ({ groupFilterOptions, initialSelection, onFilterByChange }) => {
  const [filterByValue, setFilterByValue] = useState({ value: initialSelection.id, label: initialSelection.name });

  const updateFilterByValue = useCallback((selectedValue) => {
    setFilterByValue({ filterByValue: selectedValue });
    onFilterByChange(groupFilterOptions.find(group => group.id === selectedValue.value));
  });

  return (
    <Select
      className="react-select-container"
      classNamePrefix="react-select"
      options={groupFilterOptions.map(group => ({ value: group.id, label: group.name }))}
      name="selected-filter-by"
      clearable={false}
      value={filterByValue}
      onChange={updateFilterByValue}
      searchable={true}
      placeholder={i18next.t('select')}
    />
  );
}

export default GroupingList;
